/**
 * Browser-specific WASM Module Loader
 * Supports ESM format WASM modules (generated with MODULARIZE and EXPORT_ES6)
 * Uses document.currentScript for automatic path detection in UMD builds
 */

/**
 * Gets the base path of the current script
 * Similar to Node.js __dirname
 * Handles various deployment scenarios: same directory, CDN, subdirectories
 * @returns {string} Base path of the current script (with trailing slash)
 */
export function getScriptBasePath() {
  let scriptUrl = null;
  
  // Method 1: document.currentScript (most reliable, works in sync execution)
  if (typeof document !== 'undefined' && document.currentScript?.src) {
    scriptUrl = document.currentScript.src;
  }
  
  // Method 2: Traverse script tags (fallback for async scripts)
  if (!scriptUrl && typeof document !== 'undefined') {
    const scripts = document.getElementsByTagName('script');
    // Check from end to start (most recent first)
    for (let i = scripts.length - 1; i >= 0; i--) {
      const src = scripts[i].src;
      if (src && (src.includes('facebetter.js') || src.includes('facebetter'))) {
        scriptUrl = src;
        break;
      }
    }
  }
  
  // Method 3: Try to find script by checking all scripts
  if (!scriptUrl && typeof document !== 'undefined') {
    const scripts = document.getElementsByTagName('script');
    for (let i = scripts.length - 1; i >= 0; i--) {
      const src = scripts[i].src;
      if (src) {
        // Use the last script tag as fallback
        scriptUrl = src;
        break;
      }
    }
  }
  
  // Extract base path from script URL
  if (scriptUrl) {
    try {
      const url = new URL(scriptUrl);
      return url.href.substring(0, url.href.lastIndexOf('/') + 1);
    } catch (e) {
      // If URL parsing fails, use string manipulation
      return scriptUrl.substring(0, scriptUrl.lastIndexOf('/') + 1);
    }
  }
  
  // Method 4: Fallback to current location (least reliable)
  if (typeof location !== 'undefined') {
    try {
      const url = new URL('.', location.href);
      return url.href;
    } catch (e) {
      return location.pathname.substring(0, location.pathname.lastIndexOf('/') + 1);
    }
  }
  
  // Last resort
  return './';
}

/**
 * Resolves a relative path to absolute URL
 * Handles various path formats and edge cases
 * @param {string} relativePath - Relative path (e.g., 'facebetter-core.js')
 * @param {string} basePath - Base path (with trailing slash)
 * @returns {string} Absolute URL
 */
export function resolvePath(relativePath, basePath) {
  // If relativePath is already absolute, return as-is
  if (relativePath.startsWith('http://') || relativePath.startsWith('https://') || relativePath.startsWith('//')) {
    return relativePath;
  }
  
  try {
    // Use URL API for proper resolution
    const base = basePath.endsWith('/') ? basePath : basePath + '/';
    return new URL(relativePath, base).href;
  } catch (e) {
    // Fallback: simple concatenation
    const base = basePath.endsWith('/') ? basePath : basePath + '/';
    // Remove leading ./ from relativePath if present
    const cleanPath = relativePath.replace(/^\.\//, '');
    return base + cleanPath;
  }
}

let wasmModulePromise = null;
let wasmModuleInstance = null;

/**
 * Loads the WebAssembly module (browser version)
 * Supports ESM format WASM modules generated by Emscripten with MODULARIZE and EXPORT_ES6
 * With SINGLE_FILE=1, WASM binary and data files are embedded in the JS file
 * @param {Object} [options] - Options
 * @param {string} [options.wasmUrl] - Custom WASM module URL (defaults to facebetter-core npm package)
 * @param {Function} [options.locateFile] - Custom locateFile function (usually not needed with SINGLE_FILE=1)
 * @returns {Promise<Object>} Promise that resolves with the WASM module
 */
export async function loadWasmModule(options = {}) {
  if (wasmModuleInstance) {
    return wasmModuleInstance;
  }

  if (wasmModulePromise) {
    return wasmModulePromise;
  }

  wasmModulePromise = (async () => {
    try {
      let FaceBetterModuleFactory;
      
      if (options.wasmUrl) {
        // If custom URL is provided, use it (for backward compatibility or custom builds)
        const wasmModule = await import(options.wasmUrl);
        FaceBetterModuleFactory = wasmModule.default || wasmModule.createFaceBetterModule;
      } else {
        // Try to import from facebetter-core npm package
        // Note: In UMD builds, this may not work if facebetter-core is not available
        // In that case, fallback to relative path detection
        try {
          const wasmModule = await import('facebetter-core');
          FaceBetterModuleFactory = wasmModule.default || wasmModule.createFaceBetterModule;
        } catch (e) {
          // Fallback: try to find facebetter-core.js relative to current script
          // This handles UMD builds where npm packages may not be available
          const basePath = getScriptBasePath();
          const wasmPath = resolvePath('facebetter-core.js', basePath);
          const wasmModule = await import(wasmPath);
          FaceBetterModuleFactory = wasmModule.default || wasmModule.createFaceBetterModule;
        }
      }
      
      if (!FaceBetterModuleFactory) {
        throw new Error('WASM module does not export createFaceBetterModule function');
      }

      // Configure module options
      // With SINGLE_FILE=1, WASM binary and data files are embedded in the JS file
      // No need to handle .wasm and .data file paths separately
      const moduleOptions = {
        locateFile: options.locateFile || function(path, prefix) {
          // With SINGLE_FILE=1, Emscripten handles embedded files automatically
          // Custom locateFile is only needed if user provides one
          return prefix + path;
        },
        ...options
      };

      // Initialize the module (MODULARIZE returns a Promise)
      wasmModuleInstance = await FaceBetterModuleFactory(moduleOptions);
      
      if (!wasmModuleInstance) {
        throw new Error('Failed to initialize WASM module');
      }

      wasmModuleInstance.ready = true;
      return wasmModuleInstance;
    } catch (e) {
      wasmModulePromise = null;
      throw new Error(`Failed to load WASM module: ${e.message}`);
    }
  })();

  return wasmModulePromise;
}

/**
 * Gets the current WASM module instance
 * @returns {Object} WASM module instance
 * @throws {Error} If module is not loaded
 */
export function getWasmModule() {
  if (!wasmModuleInstance) {
    throw new Error('WASM module not loaded. Call loadWasmModule() first or use BeautyEffectEngine.init()');
  }
  return wasmModuleInstance;
}

/**
 * Gets the current WASM memory buffer, handling potential reallocation
 * @returns {ArrayBuffer} Current WASM memory buffer
 */
export function getWasmBuffer() {
  const Module = getWasmModule();
  
  if (Module.buffer) {
    return Module.buffer;
  } else if (Module.HEAPU8 && Module.HEAPU8.buffer) {
    return Module.HEAPU8.buffer;
  } else if (Module.asm && Module.asm.memory) {
    return Module.asm.memory.buffer;
  }
  
  throw new Error('Unable to access WASM memory buffer');
}

